import React, { useMemo, useState } from "react";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ModernFolderCard } from "./ModernFolderCard";
import { ModernRiskCard } from "./ModernRiskCard";
import { ModernEmptyState } from "./ModernEmptyState";
import { AttachmentSectionHeader } from "./AttachmentSectionHeader";
import { SafetyRulesSection } from "./SafetyRulesSection";

import { Assessment } from "../../FolderContentsTypes";
import { ModernSafetyRoundCard } from "./ModernSafetyRoundCard";

import type { Database } from "@/integrations/supabase/types";


type FolderType = Database['public']['Tables']['folders']['Row'];

interface ModernContentGridProps {
  folderData: FolderType | null;
  subfolders: FolderType[];
  assessments: Assessment[];
  projectId: string;
  folderId: string;
  isShared: boolean;
  onOpenFolder: (folderId: string) => void;
  onNavigateToAssessment: (assessmentId: string, folderId: string) => void;
  onDeleteFolder: (folderId: string) => Promise<void>;
  onDeleteAssessment: (assessmentId: string) => Promise<void>;
  onCreateFolder: (name: string, description?: string) => Promise<void>;
  onStartAssessment?: (projectId: string, folderId: string) => void;
  ensureFolderId?: () => Promise<string | null>;
  onRefresh?: () => void;
  /** Limit the listing to one role's documents */
  only?: "basp" | "basu";
  /** Project works without an arbetsmiljöplan */
  standalone?: boolean;
  /** Antal och exempelnamn per mapp (projektnivå) */
  folderStats?: Record<string, { count: number; names: string[] }>;
  /** Visar hela projektets dokument samlat i stället för bara grundnivån */
  showAllDocuments?: boolean;
  /** Växlar samlat läge. Utelämnas växlaren inte visas */
  onToggleShowAll?: (value: boolean) => void;
}

/** Documents that only make sense when the phase works towards an arbetsmiljöplan */
const isAmpOnlyDocument = (assessment: Assessment): boolean => {
  const phase = (assessment as any).phase ?? (assessment as any).data?.phase;
  return assessment.type === "risk_assessment" && phase === "bruksskede";
};

const NO_FOLDER_KEY = "__ingen_mapp__";

export const ModernContentGrid: React.FC<ModernContentGridProps> = ({
  folderData,
  subfolders,
  assessments,
  projectId,
  folderId,
  isShared,
  onOpenFolder,
  onNavigateToAssessment,
  onDeleteFolder,
  onDeleteAssessment,
  onCreateFolder,
  onStartAssessment,
  ensureFolderId,
  onRefresh,
  only,
  standalone = false,
  folderStats,
  showAllDocuments = false,
  onToggleShowAll
}) => {
  // Group assessments by type
  const showBasP = only !== "basu";
  const showBasU = only !== "basp";
  const groupByFolder = showAllDocuments;

  // I fristående läge visas inte dokument vars enda syfte är att mata arbetsmiljöplanen
  const visible = standalone ? assessments.filter(a => !isAmpOnlyDocument(a)) : assessments;

  const riskAssessments = showBasP ? visible.filter(a => a.type === 'risk_assessment') : [];
  const skrAssessments = showBasP ? visible.filter(a => a.type === 'skr_assessment') : [];
  const workPreparations = showBasU ? visible.filter(a => a.type === 'work_preparation') : [];
  const safetyRounds = showBasU ? visible.filter(a => a.type === 'safety_round') : [];


  // Mappnamn per id, så varje dokumentkort kan visa var det ligger
  const folderNameById = useMemo(() => {
    const map = new Map<string, string>();
    subfolders.forEach(f => map.set(f.id, f.name));
    return map;
  }, [subfolders]);

  // Antal dokument per mapp, för mappkorten under Organisation
  const countByFolder = useMemo(() => {
    const counts = new Map<string, number>();
    visible.forEach(a => {
      const id = (a as any).folder_id as string | null | undefined;
      if (!id) return;
      counts.set(id, (counts.get(id) ?? 0) + 1);
    });
    return counts;
  }, [visible]);

  const hasContent = (only ? false : subfolders.length > 0) ||
    riskAssessments.length > 0 || skrAssessments.length > 0 || workPreparations.length > 0 || safetyRounds.length > 0;


  if (!hasContent && !only) {
    return (
      <div className="space-y-12">
        <ModernEmptyState
          folderData={folderData}
          projectId={projectId}
          folderId={folderId}
          isShared={isShared}
          onStartAssessment={onStartAssessment}
          onCreateFolder={onCreateFolder}
          ensureFolderId={ensureFolderId}
        />
      </div>
    );
  }


  const hasAttachments =
    skrAssessments.length > 0 || riskAssessments.length > 0 || workPreparations.length > 0;

  // Mapptillhörighet visas bara i det samlade läget – annars hör allt till grundnivån
  const showFolderLabels = showAllDocuments && subfolders.length > 0;

  const renderCard = (assessment: Assessment) => {
    const docFolderId = ((assessment as any).folder_id as string | null | undefined) ?? null;
    const folderNameForDoc = docFolderId ? folderNameById.get(docFolderId) ?? null : null;

    if (assessment.type === "safety_round") {
      return (
        <div key={assessment.id} className="animate-fade-in">
          <ModernSafetyRoundCard
            round={{
              id: assessment.id,
              name: assessment.name,
              round_date: (assessment as any).description?.replace("Skyddsrond ", "") ?? null,
              status: ((assessment as any).status as string) ?? null,
              project_id: (assessment as any).project_id ?? projectId,
              folder_id: docFolderId,
            }}
            folderName={showFolderLabels ? folderNameForDoc : undefined}
            onOpenFolder={showFolderLabels && docFolderId ? () => onOpenFolder(docFolderId) : undefined}
          />
        </div>
      );
    }

    return (
      <div key={assessment.id} className="animate-fade-in">

        <ModernRiskCard
          assessment={assessment}
          onOpen={() => onNavigateToAssessment(assessment.assessment_id || assessment.id, folderId)}
          onDelete={() => onDeleteAssessment(assessment.id)}
          projectId={projectId}
          folderId={folderId}
          onMoved={onRefresh}
          folderName={showFolderLabels ? folderNameForDoc : undefined}
          onOpenFolder={showFolderLabels && docFolderId ? () => onOpenFolder(docFolderId) : undefined}
        />
      </div>
    );
  };

  /** Renderar en dokumentgrupp, med eller utan mappindelning */
  const renderGroup = (items: Assessment[]) => {
    if (!groupByFolder || !showFolderLabels) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {items.map(renderCard)}
        </div>
      );
    }

    const buckets = new Map<string, Assessment[]>();
    items.forEach(item => {
      const key = ((item as any).folder_id as string | null | undefined) || NO_FOLDER_KEY;
      buckets.set(key, [...(buckets.get(key) ?? []), item]);
    });

    const orderedKeys = [
      ...subfolders.map(f => f.id).filter(id => buckets.has(id)),
      ...(buckets.has(NO_FOLDER_KEY) ? [NO_FOLDER_KEY] : []),
      ...[...buckets.keys()].filter(
        key => key !== NO_FOLDER_KEY && !folderNameById.has(key)
      ),
    ];

    return (
      <div className="space-y-6">
        {orderedKeys.map(key => (
          <div key={key} className="space-y-3">
            <h4 className="text-sm font-medium text-muted-foreground">
              {key === NO_FOLDER_KEY ? "Ingen mapp" : folderNameById.get(key) ?? "Annan mapp"}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {(buckets.get(key) ?? []).map(renderCard)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-12">

      {!hasAttachments && only && (
        <p className="text-sm text-muted-foreground">
          Inga dokument skapade här ännu. Använd knapparna ovan för att komma igång.
        </p>
      )}

      {!hasAttachments && onToggleShowAll && subfolders.length > 0 && (
        <div className="flex items-center gap-2">
          <Switch
            id="show-all-documents-empty"
            checked={showAllDocuments}
            onCheckedChange={onToggleShowAll}
          />
          <Label htmlFor="show-all-documents-empty" className="text-sm text-muted-foreground">
            Visa alla dokument i projektet
          </Label>
        </div>
      )}

      {/* Dokumentlista */}
      {hasAttachments && (
        <div className="space-y-8">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="border-l-2 border-primary/40 pl-4">
              <h2 className="text-lg font-semibold">
                {standalone ? "Dokument i projektet" : "Bilagor till arbetsmiljöplanen"}
              </h2>
              <p className="text-sm text-muted-foreground mt-0.5">
                {standalone
                  ? "Fristående dokument – varje dokument står för sig självt."
                  : "Underlag som arbetsmiljöplanen hänvisar till."}
              </p>
            </div>

            {onToggleShowAll && subfolders.length > 0 && (
              <div className="flex items-center gap-2">
                <Switch
                  id="show-all-documents"
                  checked={showAllDocuments}
                  onCheckedChange={onToggleShowAll}
                />
                <Label htmlFor="show-all-documents" className="text-sm text-muted-foreground">
                  Visa alla dokument i projektet
                </Label>
              </div>
            )}
          </div>

          {showBasP && <SafetyRulesSection projectId={projectId} />}

          {skrAssessments.length > 0 && (
            <section className="space-y-4">
              <AttachmentSectionHeader
                title="SKR-bedömningar"
                count={skrAssessments.length}
                doc="03"
              />
              {renderGroup(skrAssessments)}
            </section>
          )}

          {riskAssessments.length > 0 && (
            <section className="space-y-4">
              <AttachmentSectionHeader
                title="Risksammanställningar"
                count={riskAssessments.length}
                doc="11"
              />
              {renderGroup(riskAssessments)}
            </section>
          )}

          {workPreparations.length > 0 && (
            <section className="space-y-4">
              <AttachmentSectionHeader
                title="Arbetsberedningar"
                count={workPreparations.length}
                description="Bas-U · utförandeskedet"
              />
              {renderGroup(workPreparations)}
            </section>
          )}

          {safetyRounds.length > 0 && (
            <section className="space-y-4">
              <AttachmentSectionHeader
                title="Skyddsronder"
                count={safetyRounds.length}
                doc="27"
                description="Bas-U · utförandeskedet"
              />
              {renderGroup(safetyRounds)}
            </section>
          )}
        </div>
      )}

      {!hasAttachments && showBasP && <SafetyRulesSection projectId={projectId} />}


      {/* Organisation – mappar */}
      {!only && subfolders.length > 0 && (
        <div className="space-y-4">
          <div className="border-l-2 border-border pl-4">
            <h2 className="text-lg font-semibold">Mappar</h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              Etapper och andra mappar. Öppna en mapp för att se dokumenten som ligger där.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {subfolders.map(subfolder => (
              <div key={subfolder.id} className="animate-fade-in">
                <ModernFolderCard
                  folder={subfolder}
                  onOpen={() => onOpenFolder(subfolder.id)}
                  onDelete={() => onDeleteFolder(subfolder.id)}
                  assessmentCount={
                    folderStats?.[subfolder.id]?.count ?? countByFolder.get(subfolder.id) ?? 0
                  }
                  documentNames={folderStats?.[subfolder.id]?.names}
                  subfolderCount={0}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>

  );
};
